/**
 * Wrapper fino de `sonner` con dos comportamientos globales de UX:
 *
 *  1. NO se acumulan: cada mensaje nuevo CIERRA los anteriores (dismiss antes de
 *     mostrar), así solo se ve el último.
 *  2. Click para cerrar TODO: el contenido del toast es clickeable y al tocarlo
 *     cierra todos los toasts (Sonner no expone un onClick del cuerpo, por eso
 *     envolvemos el mensaje en un span clickeable).
 *
 * El color/estilo lo sigue poniendo Sonner por tipo (richColors) — error en
 * rojo, success en verde, etc. La app solo usa error/success/warning.
 *
 * Uso: `import { toast } from '@/lib/notify'` (misma API que antes).
 */
import type { ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';

type Opts = Parameters<typeof sonnerToast.error>[1];

/** Envuelve el mensaje en un contenedor clickeable que cierra TODOS los toasts. */
function clickable(message: ReactNode): ReactNode {
  return (
    <span className="block w-full cursor-pointer" onClick={() => sonnerToast.dismiss()}>
      {message}
    </span>
  );
}

function show(kind: 'error' | 'success' | 'warning', message: ReactNode, opts?: Opts) {
  sonnerToast.dismiss(); // los nuevos cierran a los anteriores → no se acumulan
  return sonnerToast[kind](clickable(message), opts);
}

export const toast = {
  error: (message: ReactNode, opts?: Opts) => show('error', message, opts),
  success: (message: ReactNode, opts?: Opts) => show('success', message, opts),
  warning: (message: ReactNode, opts?: Opts) => show('warning', message, opts),
  /** Cierra todos los toasts (o el `id` dado). */
  dismiss: sonnerToast.dismiss,
};
